export type GooglePlaceSuggestion = {
  placeId: string;
  description: string;
};

/** Google autocomplete returns `{}` when it has no matches (e.g. outdated pincode). */
export function isEmptyGoogleAutocompleteResponse(data: unknown): boolean {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return true;
  const record = data as Record<string, unknown>;
  if (Object.keys(record).length === 0) return true;
  const suggestions = record.suggestions;
  return !Array.isArray(suggestions) || suggestions.length === 0;
}

const formatGooglePlaceText = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    if (typeof record.text === 'string') return record.text;
  }
  return '';
};

const resolveGooglePlaceId = (pred: Record<string, unknown> | undefined): string => {
  if (!pred) return '';
  const direct = pred.placeId ?? pred.place_id;
  if (direct != null && String(direct).trim()) return String(direct);
  const place = pred.place;
  if (typeof place === 'string') return place.replace(/^places\//, '');
  return '';
};

const resolveGooglePlaceDescription = (pred: Record<string, unknown> | undefined): string => {
  if (!pred) return '';
  const fromText = formatGooglePlaceText(pred.text);
  if (fromText) return fromText;
  const structured = pred.structuredFormat as Record<string, unknown> | undefined;
  if (structured?.mainText) {
    const fromMain = formatGooglePlaceText(structured.mainText);
    if (fromMain) return fromMain;
  }
  return '';
};

export function mapGoogleAutocompleteSuggestions(data: unknown): GooglePlaceSuggestion[] {
  if (data == null || typeof data !== 'object') return [];

  const record = data as Record<string, unknown>;
  const payload =
    Array.isArray(record.suggestions) && record.suggestions.length > 0
      ? record
      : typeof record.body === 'object' && record.body !== null
        ? (record.body as Record<string, unknown>)
        : record;

  const suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];
  const mapped: GooglePlaceSuggestion[] = [];

  for (const item of suggestions) {
    if (item == null || typeof item !== 'object') continue;
    const suggestion = item as Record<string, unknown>;
    const pred = suggestion.placePrediction as Record<string, unknown> | undefined;
    const placeId = resolveGooglePlaceId(pred);
    const description = resolveGooglePlaceDescription(pred);
    if (!placeId || !description) continue;
    mapped.push({ placeId, description });
  }

  return mapped;
}

export async function fetchGooglePlacesAutocomplete({
  input,
  googleApiKey,
  isNativeApp = false,
}: {
  input: string;
  googleApiKey: string;
  isNativeApp?: boolean;
}): Promise<GooglePlaceSuggestion[]> {
  const autocompleteUrl = isNativeApp
    ? 'https://places.googleapis.com/v1/places:autocomplete'
    : '/api/google-places-autocomplete';

  const res = await fetch(autocompleteUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': googleApiKey,
      'X-Goog-FieldMask':
        'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
    },
    body: JSON.stringify({
      input,
      includedRegionCodes: ['in'],
    }),
  });

  if (!res.ok) return [];

  const data = await res.json();
  return mapGoogleAutocompleteSuggestions(data);
}
